import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Reservation } from '@app/_components/reservation';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class GuestArrivalService {
	
	numAdults: any[] =
		[
			{ name: '1', value: '1' },
			{ name: '2', value: '2' },
			{ name: '3', value: '3' },
			{ name: '4', value: '4' },
			{ name: '5', value: '5' },
			{ name: '6', value: '6' },
			{ name: '7', value: '7' },
			{ name: '8', value: '8' },
			{ name: '9', value: '9' },
			{ name: '10', value: '10' },
			{ name: '11', value: '11' },
			{ name: '12', value: '12' },
			{ name: '13', value: '13' },
			{ name: '14', value: '14' },
			{ name: '15', value: '15' },
			{ name: '16', value: '16' },
		];
		
	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService,
		) { }

	urlEndpoint = 'reservations';

	add(reservation: Reservation): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(reservation));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, reservation);
	}

	update(reservation: Reservation): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, reservation);
	}

	delete(reservationId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${reservationId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByAgentListFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {

		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/agent-list`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}
	
	findReservationsByShortTermRentalId(shortTermRentalId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/find-all-reservations/${shortTermRentalId}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findReservationsWith(queryString: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/reservations/find-with?query=${queryString}`).pipe(retry({ count: 2, delay: 1500 }));
	}
	
	sendSMS(smsPayload: any): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(smsPayload));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/messages/send`, smsPayload);
	}

	getPropertyTasks(shortTermRentalId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/breezeways/get-property/short-term-rental-id/${shortTermRentalId}`).pipe(retry({ count: 1, delay: 2000 }));
	}

	logArrivalSend(payload: { reservationId: number; propertyCode: string; toMobile: string; sentAt: string; autoSent: boolean }): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/messages/send`, { ...payload, sendBySms: false, logOnly: true });
	}

	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {

		let globalSearchFields = [
			{ field: 'reservationId', filter: 'contains' },
			{ field: 'orderId', filter: 'contains' },
			{ field: 'checkInDateTime', filter: 'contains' },
			{ field: 'guestProfileVO.lastName', filter: 'contains' },
			{ field: 'shortTermRentalVO.propertyCode', filter: 'contains' }
		];


		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}

			if (globalFilter) {
				// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
				// params = params.append('filter', globalFilter);
				console.log('globalFilter: ' + JSON.stringify(globalFilter));
			}

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}

			console.log('FINAL FINDBY PARAMS ' + params);
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
